<template>
  <div class="p-fluid tag-form">
    <div class="field" :class="{ 'field-error': hasError('autocomp_tag') }">
      <EditAutocomplete
        v-model="selectedTags"
        field="tag"
        title="automatedScenarios.actions.tag"
        :complete="(query) => fetchTags(query)"
      />
      <FieldErrors
        :key="componentFieldErrorsKey"
        :errors="formValidation"
        field="autocomp_tag"
      />
    </div>
    <div class="field-wrapper">
      <div class="field" :class="{ 'field-error': hasError('multiplebox_type') }">
        <ButtonGroup
          v-model="type"
          :options="options"
          title="automatedScenarios.actions.tagActionChoice"
        />
        <FieldErrors
          :key="componentFieldErrorsKey"
          :errors="formValidation"
          field="multiplebox_type"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { InsertShopsTags, ShopsTagsList } from '@/composables/shop/ShopsTags';
import type { ShopsTags, ShopsTagsInputItem } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { hasFieldError } from '@/helpers/formValidation';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import { ButtonGroup, EditAutocomplete } from '@/components/flows/operators/shared';
import type { TagData } from './index';

export default defineComponent({
  name: 'TagForm',

  components: {
    FieldErrors,
    EditAutocomplete,
    ButtonGroup,
  },

  props: {
    modelValue: {
      type: Object as PropType<TagData>,
      required: true,
    },
    formValidation: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const componentFieldErrorsKey = ref(0);

    watch(() => props.formValidation, () => {
      componentFieldErrorsKey.value += 1;
    });

    const selectedTags: any = ref(
      props.modelValue.autocomp_tag && props.modelValue.autocomp_tag.length > 0 && Object.keys(props.modelValue.autocomp_tag[0]).includes('id_shop_tag')
        ? props.modelValue.autocomp_tag
        : Object.entries(props.modelValue.autocomp_tag).map(([id, tag]) => ({
            id_shop_tag: parseInt(id, 10),
            tag,
          }))
    );

    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    // Ensure multiplebox_type is a number (may come as string from DB)
    const type = ref(typeof props.modelValue.multiplebox_type === 'string' 
      ? parseInt(props.modelValue.multiplebox_type, 10) 
      : props.modelValue.multiplebox_type ?? 0);

    const options = [
      { value: 0, label: t('automatedScenarios.actions.add') },
      { value: 1, label: t('automatedScenarios.actions.remove') },
    ];

    async function fetchTags(tagFilter: string) {
      const trimmed = (tagFilter ?? '').trim();
      let result: ShopsTags[] | null = await ShopsTagsList({ shopId: idShop.value, tag: trimmed });
      if (result === null) result = [];

      // Only create a new tag if no exact (case-sensitive) match exists.
      // The backing query uses StartsWith for autocomplete suggestions, so
      // results may include longer tags like "promotion" when searching "promo" —
      // those must not block creation of the literal "promo".
      if (trimmed.length > 0 && !result.some((t) => t.tag === trimmed)) {
        const input: ShopsTagsInputItem[] = [
          {
            id_shop: idShop.value,
            tag: trimmed,
          },
        ];
        const newShopTagId = await InsertShopsTags(input);
        if (newShopTagId) {
          result = [...result, { id_shop_tag: Number(newShopTagId), tag: trimmed }];
        }
      }
      return result;
    }

    watch([selectedTags, type], () => {
      emit('update:modelValue', {
        ...props.modelValue,
        autocomp_tag: selectedTags.value,
        multiplebox_type: type.value,
      });
    });

    const hasError = (fieldName: string) => {
      return hasFieldError(props.formValidation, fieldName);
    };

    return {
      componentFieldErrorsKey,
      fetchTags,
      type,
      options,
      selectedTags,
      hasError,
    };
  },
});
</script>

<style lang="scss" scoped>
.tag-form {
  .field-wrapper {
    margin-bottom: 1rem;
  }

  .field.field-error {
    :deep(.p-autocomplete),
    :deep(.p-inputtext),
    :deep(.p-dropdown),
    :deep(.edit-autocomplete) {
      .p-autocomplete-input,
      .p-inputtext {
        border-color: #ef4444 !important;
        
        &:focus {
          border-color: #dc2626 !important;
          box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25) !important;
        }
      }
    }

    :deep(.button-group) {
      .p-button {
        &.p-button-outlined {
          border-color: #ef4444;
        }
      }
    }
  }
}
</style>
